import React, { useState } from 'react';
import '../../assets/styles/Home.css';
import '../../assets/styles/Login.css';
import '../../assets/styles/FormCard.css';
import { useNavigate } from 'react-router-dom';
import { Building2, HandHeart, KeyRound, Lock, Mail, ShieldCheck, Ticket, User } from 'lucide-react';
import { useLanguage } from '../../assets/js/LanguageContext';
import { afterAuthPath } from '../../utils/afterAuth';
import { authErrorKey } from '../../utils/authErrors';
import { FormField, PasswordField } from '../common/FormField';
import Checkbox from '../common/Checkbox';
import Modal from '../common/Modal';
import PrivacyNotice from './PrivacyNotice';

const API = process.env.REACT_APP_API_URL;
const MIN_PASSWORD = 6;

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const hasMinLength = (value, min) => value.trim().length >= min;

const SignUpForm = () => {
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isONG, setIsONG] = useState(false);
  const [showOngPopup, setShowOngPopup] = useState(false);
  const [ongTermsAccepted, setOngTermsAccepted] = useState(false);
  const [ongModalError, setOngModalError] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [hasDiscountCode, setHasDiscountCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [message, setMessage] = useState({ text: '', isError: false });
  const [nameError, setNameError] = useState('');
  const [organizationError, setOrganizationError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [discountCodeError, setDiscountCodeError] = useState('');
  const [privacyPolicyError, setPrivacyPolicyError] = useState('');
  const [discountCodeStatus, setDiscountCodeStatus] = useState('idle');
  const [validatedDiscountCode, setValidatedDiscountCode] = useState('');
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const validateDiscountCodeWithApi = async (rawCode = discountCode) => {
    const normalizedCode = rawCode.trim();

    if (!normalizedCode) {
      setDiscountCodeStatus('idle');
      setValidatedDiscountCode('');
      setDiscountCodeError(t('auth_error_discount_required'));
      return false;
    }

    setDiscountCodeStatus('checking');
    setDiscountCodeError('');
    setErrorMessage('');

    try {
      const discountResponse = await fetch(`${API}/api/discount-codes/validate-code-only`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo_promocional: normalizedCode })
      });

      const discountData = await discountResponse.json();
      const isValidCode =
        discountResponse.ok &&
        discountData?.valid !== false &&
        discountData?.isValid !== false &&
        discountData?.codigo_valido !== false;

      if (!isValidCode) {
        setDiscountCodeStatus('invalid');
        setValidatedDiscountCode('');
        setDiscountCodeError(t('auth_error_discount_invalid'));
        return false;
      }

      setDiscountCodeStatus('valid');
      setValidatedDiscountCode(normalizedCode);
      setDiscountCodeError('');
      return true;
    } catch (discountError) {
      console.error('Error al validar código de descuento:', discountError);
      setDiscountCodeStatus('invalid');
      setValidatedDiscountCode('');
      setDiscountCodeError(t('auth_error_discount_check'));
      return false;
    }
  };

  const nameErrorFor = (value) => {
    if (!value.trim()) return t('auth_error_name_required');
    return hasMinLength(value, 2) ? '' : t('auth_error_name_short');
  };

  const organizationErrorFor = (value) => {
    if (!value.trim()) return t('auth_error_org_required');
    return hasMinLength(value, 2) ? '' : t('auth_error_org_short');
  };

  const emailErrorFor = (value) => {
    if (!value.trim()) return t('auth_error_email_required');
    return isValidEmail(value) ? '' : t('auth_error_email_invalid');
  };

  const passwordErrorFor = (value) => {
    if (!value.trim()) return t('auth_error_password_required');
    return value.length >= MIN_PASSWORD ? '' : t('auth_error_password_short');
  };

  const confirmErrorFor = (value, original) => {
    if (!value.trim()) return t('auth_error_confirm_required');
    return value === original ? '' : t('auth_error_password_mismatch');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;

    const errors = {
      name: nameErrorFor(name),
      organization: organizationErrorFor(organization),
      email: emailErrorFor(email),
      password: passwordErrorFor(password),
      confirm: confirmErrorFor(confirmPassword, password),
      privacy: acceptedPrivacy ? '' : t('auth_error_privacy_required'),
      discount: hasDiscountCode && !discountCode.trim() ? t('auth_error_discount_required') : ''
    };
    setNameError(errors.name);
    setOrganizationError(errors.organization);
    setEmailError(errors.email);
    setPasswordError(errors.password);
    setConfirmPasswordError(errors.confirm);
    setPrivacyPolicyError(errors.privacy);
    setDiscountCodeError(errors.discount);
    setErrorMessage('');
    if (errors.discount) {
      setDiscountCodeStatus('invalid');
      setValidatedDiscountCode('');
    }
    if (Object.values(errors).some(Boolean)) return;

    if (hasDiscountCode) {
      const normalizedDiscountCode = discountCode.trim();
      const alreadyValidated = discountCodeStatus === 'valid' && validatedDiscountCode === normalizedDiscountCode;
      if (!alreadyValidated && !(await validateDiscountCodeWithApi(normalizedDiscountCode))) return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          organization,
          email,
          password,
          isONG: isONG ? 1 : 0,
          discountCode: hasDiscountCode ? discountCode.trim() : null
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const key = authErrorKey(response.status === 429 ? 'too_many_attempts' : data.code, 'auth_error_generic');
        if (data.code === 'email_taken' || data.code === 'invalid_email') setEmailError(t(key));
        else if (data.code === 'password_too_short') setPasswordError(t(key));
        else setErrorMessage(t(key));
        return;
      }

      const res = await fetch(`${API}/verified`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language })
      });
      setMessage(res.ok
        ? { text: t('signup_code_sent'), isError: false }
        : { text: t('auth_error_send_code'), isError: true });
      setStep(2);
    } catch (error) {
      console.error('Error al conectar con el servidor:', error);
      setErrorMessage(t('auth_error_connection'));
    } finally {
      setSending(false);
    }
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);

    try {
      const res = await fetch(`${API}/ok-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({ text: t(authErrorKey(res.status === 429 ? 'too_many_attempts' : data.code, 'auth_error_generic')), isError: true });
        return;
      }

      const loginRes = await fetch(`${API}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const loginData = await loginRes.json().catch(() => ({}));
      if (!loginRes.ok || !loginData.token) {
        setMessage({ text: t('auth_error_login_after_code'), isError: true });
        return;
      }

      localStorage.setItem('token', loginData.token);
      if (loginData.refreshToken) {
        localStorage.setItem('refreshToken', loginData.refreshToken);
      }
      window.dispatchEvent(new Event('authChange'));
      // Si se marcó como ONG, ahora sí toca la verificación (ya con la cuenta creada)
      if (isONG) {
        navigate('/ong-verification');
        return;
      }
      // Primero las preguntas que generó en la landing, luego el plan que eligió en Precios
      navigate(afterAuthPath('/choose'));
    } catch (error) {
      console.error('Error al verificar el código:', error);
      setMessage({ text: t('auth_error_connection'), isError: true });
    } finally {
      setSending(false);
    }
  };

  const handleNameChange = (event) => {
    const { value } = event.target;
    setName(value);
    setErrorMessage('');
    setNameError(value ? nameErrorFor(value) : '');
  };

  const handleOrganizationChange = (event) => {
    const { value } = event.target;
    setOrganization(value);
    setErrorMessage('');
    setOrganizationError(value ? organizationErrorFor(value) : '');
  };

  const handleEmailChange = (event) => {
    const { value } = event.target;
    setEmail(value);
    setErrorMessage('');
    setEmailError(value ? emailErrorFor(value) : '');
  };

  const handlePasswordChange = (event) => {
    const { value } = event.target;
    setPassword(value);
    setErrorMessage('');
    setPasswordError(value ? passwordErrorFor(value) : '');
    setConfirmPasswordError(confirmPassword && value !== confirmPassword ? t('auth_error_password_mismatch') : '');
  };

  const handleConfirmPasswordChange = (event) => {
    const { value } = event.target;
    setConfirmPassword(value);
    setErrorMessage('');
    setConfirmPasswordError(value && value !== password ? t('auth_error_password_mismatch') : '');
  };

  const handleDiscountCodeChange = (event) => {
    setDiscountCode(event.target.value);
    setDiscountCodeError('');
    setDiscountCodeStatus('idle');
    setValidatedDiscountCode('');
    setErrorMessage('');
  };

  const handleDiscountCodeToggle = () => {
    const nextValue = !hasDiscountCode;
    setHasDiscountCode(nextValue);

    if (!nextValue) {
      setDiscountCode('');
      setDiscountCodeError('');
      setDiscountCodeStatus('idle');
      setValidatedDiscountCode('');
      setErrorMessage('');
    }
  };

  const handlePrivacyPolicyChange = () => {
    setAcceptedPrivacy(!acceptedPrivacy);
    setPrivacyPolicyError('');
    setErrorMessage('');
  };

  const handleOngToggle = () => {
    if (isONG) {
      setIsONG(false);
      return;
    }

    setOngModalError('');
    setShowOngPopup(true);
  };

  const handleOngLinkClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOngModalError('');
    setShowOngPopup(true);
  };

  // Marcar "somos una ONG" no interrumpe el registro: la cuenta se crea igual y la
  // verificación se pide al terminar, ya con sesión, para que quede ligada a la cuenta.
  const handleOngProceed = () => {
    if (!ongTermsAccepted) {
      setOngModalError(t('signup_ong_accept_required'));
      return;
    }

    setIsONG(true);
    setShowOngPopup(false);
    setOngModalError('');
  };

  const handleOngPopupClose = () => {
    setShowOngPopup(false);
    setOngModalError('');
  };

  return (
    <>
      {step === 1 && (
        <form className="tp-form tp-form--auth" onSubmit={handleSubmit} noValidate>
          <h2 className="tp-auth__title">{t('signup_title')}</h2>
          <div className="tp-form__grid">
            <FormField
              id="signup-name"
              label={t('signup_placeholder_name')}
              hideLabel
              icon={<User />}
              value={name}
              onChange={handleNameChange}
              placeholder={t('signup_placeholder_name')}
              autoComplete="name"
              error={nameError}
              required
            />
            <FormField
              id="signup-organization"
              label={t('signup_placeholder_organization')}
              hideLabel
              icon={<Building2 />}
              value={organization}
              onChange={handleOrganizationChange}
              placeholder={t('signup_placeholder_organization')}
              autoComplete="organization"
              error={organizationError}
              required
            />
          </div>
          <FormField
            id="email-input"
            type="email"
            label={t('signup_placeholder_email')}
            hideLabel
            icon={<Mail />}
            value={email}
            onChange={handleEmailChange}
            placeholder={t('signup_placeholder_email')}
            autoComplete="email"
            error={emailError}
            required
          />
          <PasswordField
            id="signup-password"
            label={t('signup_placeholder_password')}
            hideLabel
            icon={<Lock />}
            value={password}
            onChange={handlePasswordChange}
            placeholder={t('signup_placeholder_password')}
            autoComplete="new-password"
            error={passwordError}
            showLabel={t('form_show_password')}
            hideLabelText={t('form_hide_password')}
            required
          />
          <PasswordField
            id="signup-confirm-password"
            label={t('signup_placeholder_confirm_password')}
            hideLabel
            icon={<Lock />}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            placeholder={t('signup_placeholder_confirm_password')}
            autoComplete="new-password"
            error={confirmPasswordError}
            showLabel={t('form_show_password')}
            hideLabelText={t('form_hide_password')}
            required
          />

          <div className="tp-form__checks">
            <Checkbox checked={hasDiscountCode} onChange={handleDiscountCodeToggle}>
              {t('signup_discount_toggle')}
            </Checkbox>

            {hasDiscountCode && (
              <div className="tp-field has-icon">
                <div className="tp-field__control">
                  <span className="tp-field__icon" aria-hidden="true"><Ticket /></span>
                  <input
                    id="signup-discount"
                    type="text"
                    value={discountCode}
                    onChange={handleDiscountCodeChange}
                    onBlur={() => {
                      if (discountCode.trim()) {
                        validateDiscountCodeWithApi(discountCode);
                      }
                    }}
                    placeholder={t('signup_discount_placeholder')}
                    aria-label={t('signup_discount_placeholder')}
                    className={discountCodeError ? 'has-error' : undefined}
                    aria-invalid={discountCodeError ? true : undefined}
                  />
                </div>
                {discountCodeStatus === 'checking' && (
                  <p className="tp-field__status">{t('signup_discount_checking')}</p>
                )}
                {discountCodeStatus === 'valid' && !discountCodeError && (
                  <p className="tp-field__status is-valid">{t('signup_discount_valid')}</p>
                )}
                {discountCodeError && <p className="tp-field__error">{discountCodeError}</p>}
              </div>
            )}

            <Checkbox
              checked={acceptedPrivacy}
              onChange={handlePrivacyPolicyChange}
              aria-invalid={privacyPolicyError ? true : undefined}
            >
              {t('signup_privacy_accept')}{' '}
              <button type="button" className="tp-check__link" onClick={() => setShowPrivacyPopup(true)}>
                {t('signup_privacy_policy')}
              </button>
            </Checkbox>
            {privacyPolicyError && <p className="tp-field__error">{privacyPolicyError}</p>}

            <div className="tp-check-row">
              <Checkbox
                checked={isONG}
                onChange={handleOngToggle}
                aria-label={t('signup_is_ong')}
              />
              <button type="button" className="tp-check__link" onClick={handleOngLinkClick}>
                {t('signup_is_ong')}
              </button>
            </div>
          </div>

          {errorMessage && <p className="tp-form__alert" role="alert">{errorMessage}</p>}

          <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
            {t('signup_btn_create')}
          </button>
        </form>
      )}

      {step === 2 && (
        <form className="tp-form tp-form--auth" onSubmit={handleCodeSubmit}>
          <h2 className="tp-auth__title">{t('signup_code_title')}</h2>
          <p className="tp-form__copy tp-auth__copy">{t('signup_code_copy')}</p>
          <FormField
            id="signup-code"
            label={t('signup_code_placeholder')}
            hideLabel
            icon={<KeyRound />}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('signup_code_placeholder')}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
          {message.text && (
            <p className={message.isError ? 'tp-form__alert' : 'tp-form__note'} role={message.isError ? 'alert' : 'status'}>
              {message.text}
            </p>
          )}
          <button type="submit" className="btn btn--primary tp-form__submit" disabled={sending}>
            {t('signup_code_btn')}
          </button>
        </form>
      )}

      <Modal
        open={showOngPopup}
        onOpenChange={(open) => {
          if (!open) handleOngPopupClose();
        }}
        size="md"
        icon={<HandHeart size={18} strokeWidth={2} />}
        tone="#7C3AED"
        title={t('signup_ong_modal_title')}
        description={t('signup_ong_modal_intro')}
        cancelText={t('signup_ong_close')}
        confirmText={t('signup_ong_continue')}
        confirmDisabled={!ongTermsAccepted}
        onConfirm={() => {
          if (!ongTermsAccepted) {
            setOngModalError(t('signup_ong_accept_required'));
            return false;
          }
          handleOngProceed();
          return undefined;
        }}
      >
        <p>{t('signup_ong_modal_body')}</p>
        <p className="tp-modal__note">{t('signup_ong_modal_after')}</p>
        <p className="tp-modal__note">{t('signup_ong_modal_note')}</p>
        <Checkbox
          checked={ongTermsAccepted}
          onChange={() => {
            setOngTermsAccepted(!ongTermsAccepted);
            setOngModalError('');
          }}
        >
          {t('signup_ong_confirm')}
        </Checkbox>
        {ongModalError && <p className="tp-modal__error" role="alert">{ongModalError}</p>}
      </Modal>

      <Modal
        open={showPrivacyPopup}
        onOpenChange={setShowPrivacyPopup}
        size="lg"
        icon={<ShieldCheck size={18} strokeWidth={2} />}
        tone="#7C3AED"
        title={t('signup_privacy_modal_title')}
        cancelText={t('signup_ong_close')}
        confirmText={t('signup_privacy_accept_btn')}
        onConfirm={() => {
          setAcceptedPrivacy(true);
          setPrivacyPolicyError('');
          setErrorMessage('');
        }}
      >
        <PrivacyNotice />
      </Modal>
    </>
  );
};

export default SignUpForm;
